export const LAST_ACTIVITY_STORAGE_KEY = 'app_last_activity_at'
const MOUSEMOVE_DEBOUNCE_MS = 30000
const ACTIVITY_CHANNEL_NAME = 'app_idle_activity_v1'

export function touchLastActivityAt(storageKey = LAST_ACTIVITY_STORAGE_KEY) {
  const now = Date.now()
  try {
    localStorage.setItem(storageKey, String(now))
  } catch (e) { /* ignore */ }
  try {
    sessionStorage.removeItem(storageKey)
  } catch (e) { /* ignore */ }

  notifyActivityChannel(now)
}

export function clearLastActivityAt(storageKey = LAST_ACTIVITY_STORAGE_KEY) {
  try {
    localStorage.removeItem(storageKey)
  } catch (e) { /* ignore */ }
  try {
    sessionStorage.removeItem(storageKey)
  } catch (e) { /* ignore */ }
}

function notifyActivityChannel(timestamp) {
  try {
    if (typeof BroadcastChannel === 'undefined') return
    const channel = new BroadcastChannel(ACTIVITY_CHANNEL_NAME)
    channel.postMessage({ type: 'activity', at: timestamp })
    channel.close()
  } catch (e) { /* ignore */ }
}

function migrateLegacyActivityStorage(storageKey) {
  try {
    const legacy = sessionStorage.getItem(storageKey)
    const current = localStorage.getItem(storageKey)
    if (legacy && !current) {
      localStorage.setItem(storageKey, legacy)
    }
    if (legacy) {
      sessionStorage.removeItem(storageKey)
    }
  } catch (e) { /* ignore */ }
}

class IdleMonitor {
  constructor(options) {
    this.idleTimeout = options.idleTimeout || 4 * 60 * 60 * 1000
    this.warningTimeout = options.warningTimeout || 5 * 60 * 1000
    this.storageKey = options.storageKey || LAST_ACTIVITY_STORAGE_KEY
    this.events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click']
    this.onIdleWarning = options.onIdleWarning || (() => {})
    this.onHideWarning = options.onHideWarning || (() => {})
    this.onLogout = options.onLogout || (() => {})

    this.reconcileTimer = null
    this.logoutTimer = null
    this.countdownInterval = null
    this.activityChannel = null
    this.isWarningShown = false
    this.isLoggingOut = false
    this._lastMouseMoveTouch = 0

    this._onActivity = this._handleActivity.bind(this)
    this._onMouseMove = this._handleMouseMove.bind(this)
    this._onVisibilityChange = this._handleVisibilityChange.bind(this)
    this._onWindowFocus = this._handleVisibilityChange.bind(this)
    this._onStorage = this._handleStorage.bind(this)
    this._onActivityChannel = this._handleActivityChannel.bind(this)
  }

  start() {
    migrateLegacyActivityStorage(this.storageKey)
    if (this.readLastActivityAt() == null) {
      touchLastActivityAt(this.storageKey)
    }
    this.bindEvents()
    this.reconcile()
  }

  destroy() {
    this.unbindEvents()
    this.clearTimers()
    this.isWarningShown = false
    this.isLoggingOut = false
  }

  touchActivity() {
    touchLastActivityAt(this.storageKey)
    if (!this.isWarningShown && !this.isLoggingOut) {
      this.scheduleReconcile()
    }
  }

  readLastActivityAt() {
    try {
      let raw = localStorage.getItem(this.storageKey)
      if (!raw) {
        raw = sessionStorage.getItem(this.storageKey)
      }
      if (!raw) return null
      const n = parseInt(raw, 10)
      return Number.isFinite(n) ? n : null
    } catch (e) {
      return null
    }
  }

  getElapsedMs() {
    const last = this.readLastActivityAt()
    if (last == null) return 0
    return Date.now() - last
  }

  reconcile() {
    if (this.isLoggingOut) return

    const elapsed = this.getElapsedMs()
    const idle = this.idleTimeout
    const warn = this.warningTimeout
    const totalLimit = idle + warn

    if (elapsed >= totalLimit) {
      this.logout()
      return
    }

    if (elapsed >= idle) {
      const remainingMs = totalLimit - elapsed
      const remainingSec = Math.max(1, Math.ceil(remainingMs / 1000))
      if (!this.isWarningShown) {
        this.enterWarningState(remainingSec, remainingMs)
      } else {
        this.scheduleLogoutAt(remainingMs)
        this.emitWarning(remainingSec)
      }
      return
    }

    this.leaveWarningState()
    this.scheduleReconcile(idle - elapsed)
  }

  enterWarningState(remainingSeconds, remainingMs) {
    this.isWarningShown = true
    this.emitWarning(remainingSeconds)
    this.scheduleLogoutAt(remainingMs)
    this.startCountdownTick()
  }

  leaveWarningState() {
    if (this.isWarningShown) {
      this.isWarningShown = false
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
        this.countdownInterval = null
      }
      this.onHideWarning()
    }
  }

  emitWarning(remainingSeconds) {
    this.onIdleWarning(remainingSeconds, {
      continue: () => this.continueSession(),
      logout: () => this.logout(),
    })
  }

  /** Tick from shared last-activity timestamp (safe when another tab updates activity). */
  startCountdownTick() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
      this.countdownInterval = null
    }

    this.countdownInterval = setInterval(() => {
      if (this.isLoggingOut) return

      const elapsed = this.getElapsedMs()
      const idle = this.idleTimeout
      const warn = this.warningTimeout
      const totalLimit = idle + warn

      if (elapsed >= totalLimit) {
        this.logout()
        return
      }

      if (elapsed < idle) {
        if (this.logoutTimer) {
          clearTimeout(this.logoutTimer)
          this.logoutTimer = null
        }
        this.leaveWarningState()
        this.scheduleReconcile(idle - elapsed)
        return
      }

      const remainingMs = totalLimit - elapsed
      const remainingSec = Math.max(1, Math.ceil(remainingMs / 1000))
      this.scheduleLogoutAt(remainingMs)
      this.emitWarning(remainingSec)
    }, 1000)
  }

  continueSession() {
    if (this.isLoggingOut) return

    this.touchActivity()
    this.leaveWarningState()
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer)
      this.logoutTimer = null
    }
    this.reconcile()
  }

  logout() {
    if (this.isLoggingOut) return
    this.isLoggingOut = true
    this.leaveWarningState()
    this.clearTimers()
    this.onLogout()
  }

  scheduleReconcile(delayMs) {
    if (this.reconcileTimer) {
      clearTimeout(this.reconcileTimer)
      this.reconcileTimer = null
    }
    const delay = Math.max(0, delayMs)
    this.reconcileTimer = setTimeout(() => {
      this.reconcileTimer = null
      this.reconcile()
    }, delay)
  }

  scheduleLogoutAt(remainingMs) {
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer)
    }
    this.logoutTimer = setTimeout(() => {
      this.logoutTimer = null
      this.logout()
    }, Math.max(0, remainingMs))
  }

  clearTimers() {
    if (this.reconcileTimer) {
      clearTimeout(this.reconcileTimer)
      this.reconcileTimer = null
    }
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer)
      this.logoutTimer = null
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
      this.countdownInterval = null
    }
  }

  _handleActivity() {
    if (this.isWarningShown || this.isLoggingOut) return
    this.touchActivity()
  }

  _handleMouseMove() {
    if (this.isWarningShown || this.isLoggingOut) return
    const now = Date.now()
    if (now - this._lastMouseMoveTouch < MOUSEMOVE_DEBOUNCE_MS) return
    this._lastMouseMoveTouch = now
    this.touchActivity()
  }

  _handleVisibilityChange() {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return
    }
    this.reconcile()
  }

  _handleStorage(event) {
    if (event.storageArea !== localStorage) return
    if (event.key !== this.storageKey && event.key != null) return
    this.reconcile()
  }

  _handleActivityChannel(event) {
    if (!event || !event.data || event.data.type !== 'activity') return
    this.reconcile()
  }

  bindEvents() {
    this.events.forEach((event) => {
      window.addEventListener(event, this._onActivity, { passive: true })
    })
    window.addEventListener('mousemove', this._onMouseMove, { passive: true })
    document.addEventListener('visibilitychange', this._onVisibilityChange)
    window.addEventListener('focus', this._onWindowFocus)
    window.addEventListener('storage', this._onStorage)

    try {
      if (typeof BroadcastChannel !== 'undefined') {
        this.activityChannel = new BroadcastChannel(ACTIVITY_CHANNEL_NAME)
        this.activityChannel.addEventListener('message', this._onActivityChannel)
      }
    } catch (e) { /* ignore */ }
  }

  unbindEvents() {
    this.events.forEach((event) => {
      window.removeEventListener(event, this._onActivity)
    })
    window.removeEventListener('mousemove', this._onMouseMove)
    document.removeEventListener('visibilitychange', this._onVisibilityChange)
    window.removeEventListener('focus', this._onWindowFocus)
    window.removeEventListener('storage', this._onStorage)

    if (this.activityChannel) {
      this.activityChannel.removeEventListener('message', this._onActivityChannel)
      this.activityChannel.close()
      this.activityChannel = null
    }
  }
}

export default IdleMonitor
